
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { RequiredValidator } from "@dev/hc-components";
import ProcessEnum from "@/app/dto/ProcessEnum";
import ProcessResultKey from "@/app/dto/ProcessResultKeyEnum";
import FactorEnum from "@/app/dto/FactorEnum";
import IApiError from "@/app/model/IApiError";
import KeyResult from "@/app/model/KeyResult";
import OtpData from "@/app/model/OtpData";
import OtpResult from "@/app/model/OtpResult";
import RouteName from "@/app/router/NameEnum";
import AuthorizationService from "@/app/service/authorization";
import LoginService from "@/app/service/login";
import Countdown from "@/components/countdown";
import ILoginResponse from "@/app/dto/ILoginResponse";
import ILoginWorkflowStep from "@/app/dto/ILoginWorkflowStep";
import LoginWorkflowCodeEnum from "@/app/dto/LoginWorkflowCodeEnum";

@Component({
    components: {
        Countdown
    }
})
export default class LoginOtp extends Vue {
    @Prop({ type: String }) customerUuid!: string;
    @Prop({ type: String }) password!: string;
    @Prop({ type: String }) clientId!: string;
    @Prop({ type: String }) responseType!: string;
    @Prop({ type: String }) redirectUri!: string;

    private keyResult: KeyResult | null = null;
    private phoneNumber: string = "";
    private waiting: boolean = false;
    private otpValue: string = "";
    private otpValidator: RequiredValidator = new RequiredValidator(this.$localize("sms.required"));
    private smsInvalid: boolean = false;
    private otpErrors = [ProcessResultKey.OTP_LIMIT_REACHED, ProcessResultKey.OTP_NOT_READY_YET, ProcessResultKey.OTP_PHONE_BLOCKED];
    private submitting: boolean = false;

    private get getWaitUntilBlockId(): string {
        return this.isBlockedWaitUntil ? "withBlockUntil" : "withWaitUntil";
    }

    private get isBlockedWaitUntil(): boolean {
        return this.isNotReadyYet || this.isLimitReached || this.isPhoneBlocked || this.isAuthorizationBlocked;
    }

    private get getBlockedWaitUntilReason(): string {
        let reasonTranslationKey: string = "";
        if (this.isNotReadyYet) {
            reasonTranslationKey = "sms.not_ready_yet";
        } else if (this.isLimitReached) {
            reasonTranslationKey = "sms.too_many_attempts";
        } else if (this.isPhoneBlocked) {
            reasonTranslationKey = "sms.phone_blocked";
        } else if (this.isAuthorizationBlocked) {
            reasonTranslationKey = "sms.authorization_blocked";
        }

        return this.$localize(reasonTranslationKey);
    }

    private get isNotReadyYet(): boolean {
        return !!this.keyResult?.isNotReadyYet();
    }

    private get isLimitReached(): boolean {
        return !!this.keyResult?.isLimitReached();
    }

    private get isPhoneBlocked(): boolean {
        return !!this.keyResult?.isPhoneBlocked();
    }

    private get isAuthorizationBlocked(): boolean {
        return !!this.keyResult?.isAuthorizationBlocked();
    }

    private get isValid(): boolean {
        return this.otpValidator.validate(this.otpValue);
    }

    private onCountdownEnd(): void {
        this.waiting = false;
    }

    private onSubmit(): void {
        this.clearErrors();
        if (this.isValid) {
            this.submitting = true;
            LoginService.login(
                this.customerUuid,
                new OtpData(this.otpValue, this.keyResult?.getOtpKey() || ""),
                this.keyResult?.getAuthorizationKey() || "",
                this.password
            ).then((response: ILoginResponse) => {
                if (this.hasDashboard(response)) {
                    this.$router.push({
                        name: RouteName.LOGIN_CONSENT,
                        params: {
                            token: response.token.accessToken,
                            clientId: this.clientId,
                            responseType: this.responseType,
                            redirectUri: this.redirectUri
                        }
                    });
                } else {
                    let errorHeading: string = this.$localize("error.account.title");
                    let errorMessage: string = this.$localize("error.account.not_active.message");
                    this.routeToError({ heading: errorHeading, message: errorMessage });
                }
            }).catch((error: IApiError) => {
                this.processLoginError(error);
            })
            .finally(() => {
                this.submitting = false;
            });
        }
    }

    private hasDashboard(response: ILoginResponse): boolean {
        return !!response.workflowSteps!!.find((w: ILoginWorkflowStep) => w.code == LoginWorkflowCodeEnum.DASHBOARD);
    }

    private createKeyRequest(sendOtp: boolean): void {
        AuthorizationService.authorizationKeyExternal(this.customerUuid, [FactorEnum.PASSWORD, FactorEnum.OTP], sendOtp, ProcessEnum.LOGIN)
            .then((response: KeyResult) => {
                if (this.keyResult) {
                    this.keyResult?.mergeKey(response);
                } else {
                    this.keyResult = response;
                }
                this.processKeyResult();
            })
            .catch((error: IApiError) => {
                this.routeToError();
            });
    }

    private sendOtpRequest(): void {
        if (this.keyResult?.authKey) {
            AuthorizationService.sendOtpExternal(this.customerUuid, this.keyResult?.getAuthorizationKey() || "", ProcessEnum.LOGIN)
                .then((response: OtpResult) => {
                    this.keyResult?.mergeOtp(response);
                    this.processKeyResult();
                })
                .catch((error: IApiError) => {
                    this.routeToError();
                });
        } else {
            this.createKeyRequest(true);
        }
    }

    private processKeyResult(): void {
        if (!!this.keyResult?.authKey || this.otpErrors.includes(this.keyResult?.error?.status as ProcessResultKey)) {
            this.setupPhone();
            this.setupInitWaiting();
        } else if (this.keyResult?.isVerifiedMobileNotFound()) {
            this.$router.push({ name: RouteName.LOGIN_VERIFY_PHONE });
        } else {
            !!this.keyResult?.error ? this.processKeyError(this.keyResult?.error) : this.routeToError();
        }
    }

    private setupPhone(): void {
        if (!!this.keyResult?.getPhoneNumber()) {
            this.phoneNumber = this.keyResult?.getPhoneNumber() || "";
        }
    }

    private setupInitWaiting(): void {
        let waitUntil: Date | undefined = this.keyResult?.getWaitUntil();
        this.waiting = !!waitUntil && waitUntil > new Date();
    }

    private processKeyError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";

        switch (error.status) {
            case ProcessResultKey.ACCOUNT_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.account.blocked.message");
                break;
            case ProcessResultKey.AUTHORIZATION_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.authorization.blocked.message");
                break;
        }

        this.routeToError({ heading: errorHeading, message: errorMessage });
    }

    private processLoginError(error: IApiError): void {
        if (error.status == ProcessResultKey.AUTHORIZATION_INVALID) {
            this.createKeyRequest(false);
            this.smsInvalid = true;
        } else {
            this.routeToError();
        }
    }

    private clearErrors(): void {
        this.smsInvalid = false;
    }

    private routeToPhone(): void {
        this.$router.push({ name: RouteName.LOGIN_PHONE });
    }

    private routeToError(params?: { heading: string; message: string }) {
        this.$router.push({ name: RouteName.LOGIN_ERROR, params });
    }

    created() {
        this.createKeyRequest(true);
    }
}
